import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { useState } from "react";
import { downloadCsvReport } from "v2/pages/Insights/downloadCsvReport";
import { downloadHtmlReport } from "v2/pages/Insights/downloadHtmlReport";
import { getInsightsErrorMessage } from "v2/services/insightsErrorMessage";
import { errorMessageOf } from "v2/utils/errorMessageOf";

import { generateProgressReport } from "./monitor.service";
import { MonitorableType, MonitorSource, ProgressReport } from "./monitor.types";

const FAILURE = "Não conseguimos gerar o relatório agora. Tente de novo em instantes.";
const DOWNLOAD_FAILURE = "O relatório foi gerado, mas o download falhou. Tente gerar de novo.";
/** why: por contrato, o 404 vem da guarda de partes ("não é parte" ou contrato inexistente); por CNPJ, de escopo vazio. */
const NOT_FOUND: Record<MonitorSource["kind"], string> = {
  WORKSPACE: "Contrato não encontrado ou fora do seu acesso.",
  ORGANIZATION: "Nenhum contrato com a campanha neste escopo: não há relatório a gerar.",
};

/**
 * why: 503 e 504 trazem texto fixo do backend (`snowflakeReadFailure`), que vai à tela como veio; nos
 * outros 5xx a mensagem é a exceção crua (SQL), e `errorMessageOf` a troca pela reserva.
 * hazard: 404 sem corpo JSON é rota inexistente (Express, backend sem a #2265 ou revertido), não escopo vazio.
 */
function errorText(error: unknown, kind: MonitorSource["kind"]): string {
  const status = Number((error as { response?: { status?: number } })?.response?.status);
  if (status === 404) return getInsightsErrorMessage(error, "") ? NOT_FOUND[kind] : FAILURE;
  if (status === 503 || status === 504) return getInsightsErrorMessage(error, FAILURE);
  return errorMessageOf(error, FAILURE);
}

/**
 * "Gerar relatório de avanço" (spec contributários §5.3, §6.7 item 3): o relatório do Insights, ao vivo,
 * no escopo do monitor. A lista por titular é um segundo clique: dois downloads num clique o navegador bloqueia.
 */
export function ProgressReportButton({ source, type }: { source: MonitorSource; type: MonitorableType }) {
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState<ProgressReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function generate() {
    setLoading(true);
    setReport(null);
    setError(null);
    try {
      const r = await generateProgressReport(source, type);
      // invariant: a lista por titular já veio na resposta; falha no download do HTML não a esconde.
      setReport(r);
      await downloadHtmlReport(r.html, r.filename).catch(() => setError(DOWNLOAD_FAILURE));
    } catch (e) {
      setError(errorText(e, source.kind));
    } finally {
      setLoading(false);
    }
  }

  const csv = report?.csv && report.csvFilename ? { content: report.csv, filename: report.csvFilename } : null;
  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
      <Button
        variant="outlined"
        size="small"
        onClick={generate}
        disabled={loading}
        startIcon={loading ? <CircularProgress size={14} /> : undefined}
      >
        {loading ? "Gerando relatório..." : "Gerar relatório de avanço"}
      </Button>
      {csv && (
        <Button variant="text" size="small" onClick={() => downloadCsvReport(csv.content, csv.filename)}>
          Baixar lista por titular (CSV)
        </Button>
      )}
      {report?.csvMotivo && (
        <Typography variant="body2" color="text.secondary">{report.csvMotivo}</Typography>
      )}
      {error && (
        <Typography variant="body2" color="error" role="alert">{error}</Typography>
      )}
    </Box>
  );
}
